import React, { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * A menu drawn over the page, pinned to its trigger. The rows' list clips its overflow, so a menu
 * drawn inside a row was cut on the last ones: this one lives in a portal, opens upward when there
 * is no room below, and stays inside the viewport. Closes on outside click, Escape, or when the
 * page scrolls or resizes under it (scrolling inside the menu is fine). Above every modal (theirs go to
 * z-[2100]), since it is opened from them too.
 */
export function FloatingMenu({
    anchor, open, onClose, align = "right", width = 192, children,
}: {
    anchor: React.RefObject<HTMLElement>;
    open: boolean;
    onClose: () => void;
    align?: "left" | "right";
    width?: number;
    children: ReactNode;
}) {
    const menu = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number; up: boolean } | null>(null);

    useLayoutEffect(() => {
        if (!open || !anchor.current) return setPos(null);
        const place = () => {
            const rect = anchor.current!.getBoundingClientRect();
            const height = menu.current?.offsetHeight || 0;
            const gap = 4;
            const up = rect.bottom + gap + height > window.innerHeight - 8 && rect.top - gap - height > 8;
            const left = align === "right" ? rect.right - width : rect.left;
            setPos({
                top: up ? rect.top - gap - height : rect.bottom + gap,
                left: Math.min(Math.max(8, left), window.innerWidth - width - 8),
                up,
            });
        };
        place();
        // Measured once rendered: the first pass doesn't know the menu's height yet.
        const frame = requestAnimationFrame(place);
        return () => cancelAnimationFrame(frame);
    }, [open, anchor, align, width]);

    useEffect(() => {
        if (!open) return;
        const inside = (target: EventTarget | null) =>
            !!target && (menu.current?.contains(target as Node) || anchor.current?.contains(target as Node));
        const click = (e: MouseEvent) => !inside(e.target) && onClose();
        const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
        const scroll = (e: Event) => !inside(e.target) && onClose();
        document.addEventListener("mousedown", click);
        document.addEventListener("keydown", esc);
        window.addEventListener("scroll", scroll, true);
        window.addEventListener("resize", onClose);
        return () => {
            document.removeEventListener("mousedown", click);
            document.removeEventListener("keydown", esc);
            window.removeEventListener("scroll", scroll, true);
            window.removeEventListener("resize", onClose);
        };
    }, [open, anchor, onClose]);

    if (!open) return null;
    return createPortal(
        <div
            ref={menu}
            role="menu"
            onClick={(e) => e.stopPropagation()}
            style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }}
            className={`z-[3000] rounded-2xl border border-border bg-surface p-1.5 shadow-float transition-opacity duration-150 ${pos ? "opacity-100" : "opacity-0"}`}
        >
            {children}
        </div>,
        document.body
    );
}

export const MENU_ITEM =
    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-sans text-sm text-ink transition-colors hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
